import { isDefined } from "@clipboard-health/util-ts";
import { FacilityRoles } from "@src/appV2/FacilityUser/types";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import {
  transformPlacementApplicationSummary,
  useGetPlacementApplicationSummary,
} from "@src/appV2/PlacementApplications/api/useGetPlacementApplicationSummary";
import { useManagedWorkplaces } from "@src/appV2/PlacementCandidates/hooks/useManagedWorkplaces";
import { includes, isEmpty } from "lodash";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";

const MANAGE_JOBS_PATH = "/workplace/manage-jobs";
const SUMMARY_REFRESH_INTERVAL_MS = 5 * 60_000;

interface UsePlacementApplicationSummaryParams {
  userRoles: string[];
  isFacilityAdmin: boolean;
  isFacilityUser: boolean;
  isSystemAdmin: boolean;
  userProfile: { userId: string } | null;
  isPlacementsEnabled?: boolean;
  isPlacementsLandingPageEnabled?: boolean;
  hideBannerOnManageJobsRoute?: boolean;
}

export function usePlacementApplicationSummary({
  userRoles,
  isFacilityAdmin,
  isFacilityUser,
  isSystemAdmin,
  userProfile,
  isPlacementsEnabled = false,
  isPlacementsLandingPageEnabled = false,
  hideBannerOnManageJobsRoute = false,
}: UsePlacementApplicationSummaryParams) {
  const { pathname } = useLocation();

  const isCandidatesAwaitingReviewFeatureEnabled = useCbhFlag(
    CbhFeatureFlag.CANDIDATES_AWAITING_REVIEW_BANNER_AND_BADGE,
    { defaultValue: false }
  );

  const hasRecruitmentPermissions =
    includes(userRoles, FacilityRoles.RECRUITMENT) ||
    includes(userRoles, FacilityRoles.ADMIN) ||
    isFacilityAdmin;

  const { workplaces } = useManagedWorkplaces();

  const placementsFeatureEnabled = isPlacementsEnabled || isPlacementsLandingPageEnabled;
  const shouldShowPlacementSection = hasRecruitmentPermissions && placementsFeatureEnabled;

  const queryParams = useMemo(() => {
    if (isEmpty(workplaces)) {
      return {};
    }

    return {
      filter: {
        "workplace.id": workplaces.map((workplace) => workplace.id).join(","),
      },
    };
  }, [workplaces]);

  const shouldQuerySummary =
    isDefined(userProfile?.userId) && shouldShowPlacementSection && !isEmpty(workplaces);

  const { data: placementSummary } = useGetPlacementApplicationSummary(
    {
      workplaceId: userProfile?.userId ?? "",
      queryParams,
    },
    {
      enabled: shouldQuerySummary,
      staleTime: SUMMARY_REFRESH_INTERVAL_MS,
      refetchInterval: SUMMARY_REFRESH_INTERVAL_MS,
      select: transformPlacementApplicationSummary,
    }
  );

  const needsReviewCount = placementSummary?.applied ?? 0;
  const hasCandidatesAwaitingReview = needsReviewCount > 0;
  const isOnManageJobsRoute = pathname === MANAGE_JOBS_PATH;
  const baseBannerVisible =
    isCandidatesAwaitingReviewFeatureEnabled &&
    hasCandidatesAwaitingReview &&
    ((hasRecruitmentPermissions && isFacilityUser) || isSystemAdmin);

  const shouldShowPlacementBanner = hideBannerOnManageJobsRoute
    ? baseBannerVisible && !isOnManageJobsRoute
    : baseBannerVisible;

  return {
    shouldShowPlacementBanner,
    needsReviewCount,
  };
}
